import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link, useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import {
  canAccessSurveyManage,
  canAccessSurveyResponses,
  SDA_VIEW_SURVEY,
} from '../../constants/surveyAccessPermissions';
import { useSurveyRuntimeApi } from '../context/SurveyRuntimeApiContext';
import { getApiErrorMessage } from '../api/runtimeErrors';
import { Toast } from '../components/SurveyToast';
import SurveyShareDialog from '../components/SurveyShareDialog';
import SurveyLinkDialog from '../components/SurveyLinkDialog';
import { useSurveyUiToast } from '../hooks/useSurveyUiToast';
import Pagination from '../../components/common/Pagination';
import SearchTextField from '../../components/common/SearchTextField';
import { FONT_FAMILY_PRIMARY } from '../../constants/fonts';

const PAGE_SIZE = 12;

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function localTimeToUtcHHMM(t) {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

function ScheduleRange({ opensAt, closesAt }) {
  if (!opensAt && !closesAt) return <>—</>;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography variant="body2">{formatDateTime(opensAt)}</Typography>
      <Typography variant="body2" color="text.secondary">
        to {formatDateTime(closesAt)}
      </Typography>
    </Box>
  );
}

function RecurrenceWindow({ survey }) {
  const freq = survey.recurrence_frequency || 'recurring';
  const n = survey.recurrence_instance_count ?? 0;
  const label = freq.charAt(0).toUpperCase() + freq.slice(1);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {(survey.opens_at_utc || survey.closes_at_utc) && (
        <ScheduleRange opensAt={survey.opens_at_utc} closesAt={survey.closes_at_utc} />
      )}
      <Typography component="span" variant="caption" sx={{ fontWeight: 600 }}>
        {label} · {n} cycle{n === 1 ? '' : 's'}
      </Typography>
    </Box>
  );
}

// Single lifecycle status for a survey: draft | live | scheduled | closed.
function getSurveyStatus(survey) {
  const state = survey.live_window_state;

  // A recurrence parent is stored as a "draft" template — its real status comes
  // from the current/next cycle's live window; it must never render as "Draft".
  if (survey.is_recurrence_parent) {
    if (state === 'open' || survey.is_live_window_open) {
      return { key: 'live', label: 'Live', className: 'live-pill' };
    }
    if (state === 'closed') {
      return { key: 'closed', label: 'Closed', className: 'status-closed' };
    }
    return { key: 'scheduled', label: 'Scheduled', className: 'scheduled-pill' };
  }

  if (survey.status === 'draft') {
    return { key: 'draft', label: 'Draft', className: 'status-draft' };
  }
  if (state === 'open' || survey.is_live_window_open) {
    return { key: 'live', label: 'Live', className: 'live-pill' };
  }
  if (state === 'not_open') {
    return { key: 'scheduled', label: 'Scheduled', className: 'scheduled-pill' };
  }
  if (state === 'closed' || survey.status === 'closed') {
    return { key: 'closed', label: 'Closed', className: 'status-closed' };
  }
  if (survey.status === 'published') {
    return { key: 'live', label: 'Live', className: 'live-pill' };
  }
  return { key: 'closed', label: 'Closed', className: 'status-closed' };
}

function SurveyStatus({ survey }) {
  const status = getSurveyStatus(survey);
  return <span className={`pill ${status.className}`}>{status.label}</span>;
}

function isSurveyClosed(survey) {
  return getSurveyStatus(survey).key === 'closed';
}

function isoToDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function defaultCloseDatetimeLocal(opensLocal) {
  const base = opensLocal ? new Date(opensLocal) : new Date();
  if (Number.isNaN(base.getTime())) return '';
  base.setDate(base.getDate() + 7);
  base.setMinutes(base.getMinutes() - base.getTimezoneOffset());
  return base.toISOString().slice(0, 16);
}

function defaultOpenDatetimeLocal() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function formatUtcPreview(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

const DATE_FILTERS = [
  { value: 'all', label: 'Any time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '1m', label: 'Last month' },
  { value: '3m', label: 'Last 3 months' },
  { value: '6m', label: 'Last 6 months' },
  { value: '1y', label: 'Last year' },
];

function dateFilterCutoff(value) {
  if (value === 'all') return null;
  const d = new Date();
  switch (value) {
    case '7d': d.setDate(d.getDate() - 7); break;
    case '1m': d.setMonth(d.getMonth() - 1); break;
    case '3m': d.setMonth(d.getMonth() - 3); break;
    case '6m': d.setMonth(d.getMonth() - 6); break;
    case '1y': d.setFullYear(d.getFullYear() - 1); break;
    default: return null;
  }
  return d;
}

function canManageSchedule(survey) {
  if (survey.is_recurrence_parent) return true;
  if (survey.status === 'draft') return true;
  return survey.status === 'published' || survey.status === 'closed';
}

const STATUS_SORT_ORDER = { live: 0, scheduled: 1, draft: 2, closed: 3 };

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'live', label: 'Live' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'closed', label: 'Closed' },
];

const STATUS_LABELS = Object.fromEntries(STATUS_OPTIONS.map((o) => [o.value, o.label]));

export default function SurveyManagePage() {
  const { apiFetch } = useSurveyRuntimeApi();
  const navigate = useNavigate();
  const { hasPermission, currentTenant } = useTenant();
  // The Client column only applies to the JLL Master Tenant (surveys created on
  // behalf of clients); client tenants see their own surveys, so it is hidden.
  const showClientColumn =
    (currentTenant?.tenantCode || '').toUpperCase() === 'JLL' ||
    /jll\s*master/i.test(currentTenant?.tenantName || '');
  const { showError } = useSurveyUiToast();
  const [tab, setTab] = useState('all');
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState('');
  // Multi-select filters: empty array = no filter (all).
  const [statusFilter, setStatusFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState('all');
  const [createdByFilter, setCreatedByFilter] = useState([]);
  const [orderBy, setOrderBy] = useState('updated');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);
  // Recurrence expansion: which parent rows are open + lazily-loaded children.
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [childrenCache, setChildrenCache] = useState({});
  const [copyToastKey, setCopyToastKey] = useState(0);
  const [confirmEndLiveId, setConfirmEndLiveId] = useState(null);
  const [confirmAnchorEl, setConfirmAnchorEl] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmDeleteAnchor, setConfirmDeleteAnchor] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleSurvey, setScheduleSurvey] = useState(null);
  const [scheduleOpensAt, setScheduleOpensAt] = useState('');
  const [scheduleClosesAt, setScheduleClosesAt] = useState('');
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleMode, setScheduleMode] = useState('once');
  const [recurrenceFrequency, setRecurrenceFrequency] = useState('monthly');
  const [windowDurationDays, setWindowDurationDays] = useState(7);
  const [dailyOpenTime, setDailyOpenTime] = useState('09:00');
  const [dailyCloseTime, setDailyCloseTime] = useState('17:00');
  const [seriesEndsAt, setSeriesEndsAt] = useState('');
  const [excludeWeekends, setExcludeWeekends] = useState(false);
  const [shareSurvey, setShareSurvey] = useState(null);
  const [linkSurvey, setLinkSurvey] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuSurvey, setMenuSurvey] = useState(null);
  const [promptCloneSurvey, setPromptCloneSurvey] = useState(null);
  const [promptCloneTitle, setPromptCloneTitle] = useState('');
  const [promptCloneSaving, setPromptCloneSaving] = useState(false);
  const [promptCloneError, setPromptCloneError] = useState(null);
  const canAuthor = canAccessSurveyManage(hasPermission);
  const canPreview = hasPermission(SDA_VIEW_SURVEY);
  const canViewResponses = canAccessSurveyResponses(hasPermission);
  const showActions = canAuthor || canPreview || canViewResponses;

  // All surveys are loaded ONCE on mount (and after mutations). The
  // "Live now" toggle and every filter work client-side on this list —
  // no refetch when switching tabs.
  const load = useCallback(async () => {
    setErr(null);
    try {
      const data = await apiFetch('/admin/surveys');
      setRows(data);
    } catch (e) {
      const message = getApiErrorMessage(e, 'Failed to load surveys.');
      setErr(message);
      showError(message);
    }
  }, [apiFetch, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  // ------------------------------------------------ recurrence children

  const toggleExpand = useCallback(
    async (survey) => {
      const id = survey.id;
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      if (childrenCache[id] || expandedIds.has(id)) return;
      setChildrenCache((prev) => ({ ...prev, [id]: { loading: true, runs: [] } }));
      try {
        const detail = await apiFetch(`/admin/surveys/${id}/recurrence`);
        setChildrenCache((prev) => ({
          ...prev,
          [id]: { loading: false, runs: detail?.runs ?? [] },
        }));
      } catch (e) {
        setChildrenCache((prev) => ({ ...prev, [id]: { loading: false, runs: [], error: true } }));
        showError(getApiErrorMessage(e, 'Failed to load survey cycles.'));
      }
    },
    [apiFetch, childrenCache, expandedIds, showError],
  );

  // ----------------------------------------------------------- actions

  async function endLive(id) {
    try {
      await apiFetch(`/admin/surveys/${id}/end-live`, { method: 'POST' });
      void load();
    } catch (e) {
      const message = getApiErrorMessage(e, 'Failed to end the live survey.');
      setErr(message);
      showError(message);
    } finally {
      setConfirmEndLiveId(null);
      setConfirmAnchorEl(null);
    }
  }

  async function deleteSurvey(id) {
    try {
      await apiFetch(`/admin/surveys/${id}`, { method: 'DELETE' });
      void load();
    } catch (e) {
      const message = getApiErrorMessage(e, 'Failed to delete the survey.');
      setErr(message);
      showError(message);
    } finally {
      setConfirmDeleteId(null);
      setConfirmDeleteAnchor(null);
    }
  }

  async function cloneSurvey(id, title) {
    try {
      const body = title ? JSON.stringify({ title }) : undefined;
      const cloned = await apiFetch(`/admin/surveys/${id}/clone`, { method: 'POST', body });
      navigate(`/survey/${cloned.id}/edit`, { replace: true });
    } catch (e) {
      const message = getApiErrorMessage(e, 'Failed to clone the survey.');
      setErr(message);
      showError(message);
    }
  }

  async function pauseRecurrence(parentId) {
    try {
      await apiFetch(`/admin/surveys/${parentId}/recurrence/pause`, { method: 'POST' });
      setChildrenCache((prev) => {
        const next = { ...prev };
        delete next[parentId];
        return next;
      });
      void load();
    } catch (e) {
      showError(getApiErrorMessage(e, 'Failed to pause recurrence.'));
    }
  }

  function openScheduleModal(survey) {
    const opens = isoToDatetimeLocal(survey.opens_at_utc) || defaultOpenDatetimeLocal();
    const closes = isoToDatetimeLocal(survey.closes_at_utc) || defaultCloseDatetimeLocal(opens);
    setScheduleSurvey(survey);
    setScheduleOpensAt(opens);
    setScheduleClosesAt(closes);
    setScheduleMode(survey.is_recurrence_parent ? 'recurring' : 'once');
    setRecurrenceFrequency(survey.recurrence_frequency || 'monthly');
    setSeriesEndsAt('');
    setExcludeWeekends(false);
    setScheduleOpen(true);
  }

  function closeScheduleModal() {
    if (scheduleSaving) return;
    setScheduleOpen(false);
    setScheduleSurvey(null);
  }

  async function saveSchedule(e) {
    e.preventDefault();
    if (!scheduleSurvey?.id) return;

    if (scheduleMode === 'recurring') {
      if (!scheduleOpensAt) return;
      const isDaily = recurrenceFrequency === 'daily';
      if (isDaily) {
        if (!dailyOpenTime || !dailyCloseTime) return;
        if (dailyOpenTime === dailyCloseTime) {
          showError('Daily open and close times must be different.');
          return;
        }
      }
      if (seriesEndsAt && new Date(seriesEndsAt) <= new Date(scheduleOpensAt)) {
        showError('Series end must be after the series start.');
        return;
      }
      setScheduleSaving(true);
      try {
        await apiFetch(`/admin/surveys/${scheduleSurvey.id}/recurrence`, {
          method: 'POST',
          body: JSON.stringify({
            frequency: recurrenceFrequency,
            window_duration_days: isDaily ? 1 : windowDurationDays,
            daily_open_time: isDaily ? localTimeToUtcHHMM(dailyOpenTime) : null,
            daily_close_time: isDaily ? localTimeToUtcHHMM(dailyCloseTime) : null,
            exclude_weekends: isDaily ? false : excludeWeekends,
            series_starts_at_utc: new Date(scheduleOpensAt).toISOString(),
            series_ends_at_utc: seriesEndsAt ? new Date(seriesEndsAt).toISOString() : null,
            spawn_current_period: true,
          }),
        });
        setScheduleOpen(false);
        setScheduleSurvey(null);
        void load();
      } catch (err2) {
        showError(getApiErrorMessage(err2, 'Failed to set up recurring schedule.'));
      } finally {
        setScheduleSaving(false);
      }
      return;
    }

    if (scheduleSurvey.is_recurrence_parent) {
      showError('This survey uses a recurring schedule. Expand the row to see its cycles.');
      return;
    }

    if (!scheduleOpensAt || !scheduleClosesAt) return;
    const opens = new Date(scheduleOpensAt);
    const closes = new Date(scheduleClosesAt);
    if (closes <= opens) {
      showError('Close time must be after open time.');
      return;
    }
    setScheduleSaving(true);
    try {
      await apiFetch(`/admin/surveys/${scheduleSurvey.id}/schedule`, {
        method: 'PATCH',
        body: JSON.stringify({
          opens_at_utc: opens.toISOString(),
          closes_at_utc: closes.toISOString(),
        }),
      });
      setScheduleOpen(false);
      setScheduleSurvey(null);
      void load();
    } catch (err2) {
      showError(getApiErrorMessage(err2, 'Failed to update schedule.'));
    } finally {
      setScheduleSaving(false);
    }
  }

  useEffect(() => {
    if (!scheduleOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [scheduleOpen]);

  // ------------------------------------------------------- row menu

  function openRowMenu(e, survey) {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuSurvey(survey);
  }

  function closeRowMenu() {
    setMenuAnchorEl(null);
    setMenuSurvey(null);
  }

  function handleShareCopyError() {
    const message = 'Failed to copy link.';
    setErr(message);
    showError(message);
  }

  const confirmOpen = Boolean(confirmAnchorEl) && confirmEndLiveId !== null;
  const confirmDeleteOpen = Boolean(confirmDeleteAnchor) && confirmDeleteId !== null;

  // -------------------------------------------- filter + sort + page

  const creators = useMemo(() => {
    const set = new Set();
    (rows ?? []).forEach((s) => {
      if (s.created_by) set.add(s.created_by);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!rows) return rows;
    const term = search.trim().toLowerCase();
    const cutoff = dateFilterCutoff(dateFilter);
    return rows.filter((s) => {
      if (tab === 'live' && getSurveyStatus(s).key !== 'live') return false;
      if (term) {
        const inTitle = (s.title || '').toLowerCase().includes(term);
        const inId = String(s.id).includes(term);
        if (!inTitle && !inId) return false;
      }
      // Multi-select: empty selection = all; otherwise match ANY selected value.
      if (statusFilter.length > 0 && !statusFilter.includes(getSurveyStatus(s).key)) return false;
      if (createdByFilter.length > 0 && !createdByFilter.includes(s.created_by || '')) return false;
      if (cutoff) {
        const createdRaw = s.created_at || s.updated_at;
        const created = createdRaw ? new Date(createdRaw) : null;
        if (!created || Number.isNaN(created.getTime()) || created < cutoff) return false;
      }
      return true;
    });
  }, [rows, tab, search, statusFilter, dateFilter, createdByFilter]);

  const sortedRows = useMemo(() => {
    if (!filteredRows) return filteredRows;
    const dir = order === 'asc' ? 1 : -1;
    const list = [...filteredRows];
    list.sort((a, b) => {
      switch (orderBy) {
        case 'title':
          return dir * (a.title || '').localeCompare(b.title || '');
        case 'client':
          return dir * (a.client_name || '').localeCompare(b.client_name || '');
        case 'status':
          return dir * ((STATUS_SORT_ORDER[getSurveyStatus(a).key] ?? 9) - (STATUS_SORT_ORDER[getSurveyStatus(b).key] ?? 9));
        case 'createdBy':
          return dir * (a.created_by || '').localeCompare(b.created_by || '');
        case 'updated':
        default: {
          const ta = new Date(a.updated_at || 0).getTime();
          const tb = new Date(b.updated_at || 0).getTime();
          return dir * (ta - tb);
        }
      }
    });
    return list;
  }, [filteredRows, orderBy, order]);

  const totalItems = sortedRows?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const pagedRows = useMemo(() => {
    if (!sortedRows) return sortedRows;
    const start = (page - 1) * PAGE_SIZE;
    return sortedRows.slice(start, start + PAGE_SIZE);
  }, [sortedRows, page]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, dateFilter, createdByFilter, tab]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  function handleSort(column) {
    if (orderBy === column) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrder(column === 'updated' ? 'desc' : 'asc');
    }
  }

  const hasActiveFilters =
    search.trim() !== '' || statusFilter.length > 0 || dateFilter !== 'all' || createdByFilter.length > 0;

  function clearFilters() {
    setSearch('');
    setStatusFilter([]);
    setDateFilter('all');
    setCreatedByFilter([]);
  }

  function handleRowClick(s) {
    if (s.is_recurrence_parent) {
      void toggleExpand(s);
      return;
    }
    if (canAuthor && !isSurveyClosed(s)) {
      navigate(`/survey/${s.id}/edit`);
    } else if (canViewResponses) {
      navigate(`/survey/${s.id}/responses`);
    } else if (canPreview) {
      navigate(`/survey/${s.id}/preview`);
    }
  }

  function handleChildClick(run) {
    if (canViewResponses) {
      navigate(`/survey/${run.child_survey_id}/responses`);
    }
  }

  const columnCount = 6 + (showClientColumn ? 1 : 0) + (showActions ? 1 : 0);

  const headCellSx = { fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } };
  const bodyCellSx = { fontSize: { xs: '0.875rem', md: '1rem' } };
  const hideXs = { display: { xs: 'none', md: 'table-cell' } };

  return (
    <Box sx={{ minHeight: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h5">Manage surveys</Typography>
          <Typography variant="body2" color="text.secondary">
            Create drafts, publish with a response window, and expand recurring surveys to see their cycles.
          </Typography>
        </Box>
        {canAuthor && (
          <Button
            component={Link}
            to="/survey/new"
            variant="contained"
            size="small"
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#000000',
              color: '#ffffff',
              px: 2.5,
              '&:hover': { backgroundColor: '#333333', color: '#ffffff' },
            }}
          >
            + New survey
          </Button>
        )}
      </Box>

      {/* Scope toggle — same idiom as the dashboards screen */}
      <ToggleButtonGroup
        value={tab}
        exclusive
        onChange={(e, value) => {
          if (!value || value === tab) return;
          setTab(value); // client-side filter — no refetch
        }}
        size="small"
        sx={{ mb: 2, flexWrap: 'wrap' }}
        aria-label="Survey list scope"
      >
        <ToggleButton value="all" sx={{ fontFamily: FONT_FAMILY_PRIMARY, textTransform: 'none' }}>
          All surveys
        </ToggleButton>
        <ToggleButton value="live" sx={{ fontFamily: FONT_FAMILY_PRIMARY, textTransform: 'none' }}>
          Live now
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Filters: search takes 50% of the row on desktop; the three selects
          share the other half so everything fits in one row. On tablets the
          selects wrap two-per-line; on phones everything stacks full-width. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
        }}
      >
        <SearchTextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch('')}
          placeholder="Search by ID or name…"
          aria-label="Search surveys by ID or name"
          sx={{
            flexBasis: { xs: '100%', md: 'calc(50% - 12px)' },
            flexGrow: 0,
            flexShrink: 0,
          }}
        />
        <FormControl
          size="small"
          sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 0' }, minWidth: 110 }}
        >
          <InputLabel id="survey-status-filter-label">Status</InputLabel>
          <Select
            labelId="survey-status-filter-label"
            label="Status"
            multiple
            value={statusFilter}
            onChange={(e) => {
              const v = e.target.value;
              setStatusFilter(typeof v === 'string' ? v.split(',') : v);
            }}
            renderValue={(selected) => selected.map((v) => STATUS_LABELS[v] || v).join(', ')}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} dense>
                <Checkbox size="small" checked={statusFilter.includes(opt.value)} sx={{ py: 0, pl: 0 }} />
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          size="small"
          sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 0' }, minWidth: 120 }}
        >
          <InputLabel id="survey-date-filter-label">Date created</InputLabel>
          <Select
            labelId="survey-date-filter-label"
            label="Date created"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            {DATE_FILTERS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          size="small"
          sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 0' }, minWidth: 120 }}
        >
          <InputLabel id="survey-creator-filter-label">Created by</InputLabel>
          <Select
            labelId="survey-creator-filter-label"
            label="Created by"
            multiple
            value={createdByFilter}
            onChange={(e) => {
              const v = e.target.value;
              setCreatedByFilter(typeof v === 'string' ? v.split(',') : v);
            }}
            renderValue={(selected) => selected.join(', ')}
          >
            {creators.map((c) => (
              <MenuItem key={c} value={c} dense>
                <Checkbox size="small" checked={createdByFilter.includes(c)} sx={{ py: 0, pl: 0 }} />
                <ListItemText primary={c} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {hasActiveFilters && (
          <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none', flexShrink: 0 }}>
            Clear filters
          </Button>
        )}
      </Box>

      {copyToastKey > 0 && (
        <Toast
          key={copyToastKey}
          message="Link copied to clipboard."
          variant="success"
          duration={2000}
          onDismiss={() => setCopyToastKey(0)}
        />
      )}

      {!rows && !err ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading surveys…
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer
            component={Paper}
            elevation={1}
            sx={{ borderRadius: '8px', overflow: 'auto', maxWidth: '100%' }}
          >
            <Table sx={{ minWidth: 650 }} aria-label="Surveys">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  {/* expander column */}
                  <TableCell padding="checkbox" sx={{ width: 48 }} />
                  <TableCell sx={headCellSx}>
                    <TableSortLabel
                      active={orderBy === 'title'}
                      direction={orderBy === 'title' ? order : 'asc'}
                      onClick={() => handleSort('title')}
                      sx={{ fontWeight: 600 }}
                    >
                      Title
                    </TableSortLabel>
                  </TableCell>
                  {showClientColumn && (
                    <TableCell sx={headCellSx}>
                      <TableSortLabel
                        active={orderBy === 'client'}
                        direction={orderBy === 'client' ? order : 'asc'}
                        onClick={() => handleSort('client')}
                        sx={{ fontWeight: 600 }}
                      >
                        Client
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell sx={headCellSx}>
                    <TableSortLabel
                      active={orderBy === 'status'}
                      direction={orderBy === 'status' ? order : 'asc'}
                      onClick={() => handleSort('status')}
                      sx={{ fontWeight: 600 }}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...headCellSx, ...hideXs }}>
                    <TableSortLabel
                      active={orderBy === 'createdBy'}
                      direction={orderBy === 'createdBy' ? order : 'asc'}
                      onClick={() => handleSort('createdBy')}
                      sx={{ fontWeight: 600 }}
                    >
                      Created by
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={headCellSx}>Survey link</TableCell>
                  <TableCell sx={{ ...headCellSx, ...hideXs }}>Response window</TableCell>

                  {showActions && <TableCell padding="none" sx={{ width: 48 }} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {(pagedRows ?? []).map((s) => {
                  const expanded = expandedIds.has(s.id);
                  const children = childrenCache[s.id];
                  return (
                    <React.Fragment key={s.id}>
                      <TableRow
                        hover
                        onClick={() => handleRowClick(s)}
                        sx={{
                          cursor: 'pointer',
                          // Highlight the parent row while its cycles are expanded.
                          ...(expanded && {
                            backgroundColor: 'action.selected',
                            '& > td': { borderBottom: 'none' },
                          }),
                        }}
                      >
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          {s.is_recurrence_parent ? (
                            <Tooltip title={expanded ? 'Hide cycles' : 'Show cycles'}>
                              <IconButton
                                size="small"
                                aria-label={expanded ? 'Collapse child surveys' : 'Expand child surveys'}
                                aria-expanded={expanded}
                                onClick={() => void toggleExpand(s)}
                              >
                                {/* ">" when collapsed, rotates to "v" when expanded */}
                                <ChevronRightIcon
                                  fontSize="small"
                                  sx={{
                                    transition: 'transform 150ms ease',
                                    transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              #{s.id}
                            </Typography>
                            <Typography component="span" sx={{ fontSize: 'inherit' }}>
                              {s.title}
                            </Typography>
                            {s.is_recurrence_parent && <span className="pill recurring-pill">Recurring</span>}
                          </Box>
                        </TableCell>
                        {showClientColumn && (
                          <TableCell sx={bodyCellSx}>{s.client_name || '—'}</TableCell>
                        )}
                        <TableCell sx={bodyCellSx}>
                          <SurveyStatus survey={s} />
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, ...hideXs }}>{s.created_by || '—'}</TableCell>
                        <TableCell sx={bodyCellSx} onClick={(e) => e.stopPropagation()}>
                          {s.active_public_token ? (
                            <Typography
                              component="button"
                              type="button"
                              onClick={() => setLinkSurvey(s)}
                              sx={{
                                background: 'none',
                                border: 'none',
                                p: 0,
                                cursor: 'pointer',
                                color: 'primary.main',
                                textDecoration: 'underline',
                                fontSize: 'inherit',
                                fontFamily: 'inherit',
                                '&:hover': { textDecoration: 'none' },
                              }}
                              aria-label={`View survey link for ${s.title}`}
                            >
                              View link
                            </Typography>
                          ) : (
                            <Typography component="span" variant="body2" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, ...hideXs }}>
                          {s.is_recurrence_parent ? (
                            <RecurrenceWindow survey={s} />
                          ) : (
                            <Typography variant="body2">
                              <ScheduleRange opensAt={s.opens_at_utc} closesAt={s.closes_at_utc} />
                            </Typography>
                          )}
                        </TableCell>

                        {showActions && (
                          <TableCell padding="none" onClick={(e) => e.stopPropagation()}>
                            <IconButton size="small" aria-label="Survey actions" onClick={(e) => openRowMenu(e, s)}>
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>

                      {/* Child surveys (recurrence cycles) — contained panel nested
                          under the highlighted parent row */}
                      {s.is_recurrence_parent && (
                        <TableRow>
                          <TableCell
                            colSpan={columnCount}
                            sx={{
                              p: 0,
                              borderBottom: expanded ? undefined : 'none',
                              backgroundColor: expanded ? 'rgba(0, 0, 0, 0.02)' : undefined,
                            }}
                          >
                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                              {/* Full-bleed subsection: flush with the parent row, no side gaps */}
                              <Box>
                                  <Box
                                    sx={{
                                      px: 2,
                                      py: 1,
                                      backgroundColor: '#f5f5f5',
                                      borderBottom: '1px solid',
                                      borderColor: 'divider',
                                      display: 'flex',
                                      alignItems: 'baseline',
                                      gap: 1,
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      Cycles
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {(children?.runs?.length ?? 0)} cycle
                                      {(children?.runs?.length ?? 0) === 1 ? '' : 's'} of “{s.title}”
                                      {s.recurrence_frequency ? ` · ${s.recurrence_frequency}` : ''}
                                    </Typography>
                                  </Box>
                                  {children?.loading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 2 }}>
                                      <CircularProgress size={18} />
                                      <Typography variant="body2" color="text.secondary">
                                        Loading cycles…
                                      </Typography>
                                    </Box>
                                  ) : (children?.runs?.length ?? 0) === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
                                      No cycles spawned yet — the next cycle is created automatically when its
                                      period starts.
                                    </Typography>
                                  ) : (
                                    <Table size="small" aria-label={`Cycles of ${s.title}`}>
                                      <TableHead>
                                        <TableRow
                                          sx={{
                                            '& th': {
                                              backgroundColor: '#fafafa',
                                              fontWeight: 600,
                                              fontSize: '0.75rem',
                                              color: 'text.secondary',
                                              textTransform: 'uppercase',
                                              letterSpacing: '0.04em',
                                              py: 0.75,
                                            },
                                          }}
                                        >
                                          <TableCell>Cycle</TableCell>
                                          <TableCell>Status</TableCell>
                                          <TableCell sx={hideXs}>Response window</TableCell>
                                          <TableCell align="right">Responses</TableCell>
                                          <TableCell padding="none" sx={{ width: 40 }} />
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {children.runs.map((run) => (
                                          <TableRow
                                            key={run.id}
                                            hover
                                            onClick={() => handleChildClick(run)}
                                            sx={{
                                              cursor: canViewResponses ? 'pointer' : 'default',
                                              '&:last-child td': { borderBottom: 'none' },
                                            }}
                                          >
                                            <TableCell>
                                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
                                                  {run.period_label || run.child_survey_title}
                                                </Typography>
                                                <Typography component="span" variant="caption" color="text.secondary">
                                                  #{run.child_survey_id} · {run.child_survey_title}
                                                </Typography>
                                              </Box>
                                            </TableCell>
                                            <TableCell>
                                              <SurveyStatus
                                                survey={{
                                                  status: 'published',
                                                  live_window_state: run.live_window_state,
                                                  is_live_window_open: run.is_live_window_open,
                                                  opens_at_utc: run.opens_at_utc,
                                                  is_recurrence_parent: false,
                                                }}
                                              />
                                            </TableCell>
                                            <TableCell sx={hideXs}>
                                              <Typography variant="body2" color="text.secondary">
                                                {formatDate(run.opens_at_utc)} → {formatDate(run.closes_at_utc)}
                                              </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {run.response_count}
                                              </Typography>
                                            </TableCell>
                                            <TableCell padding="none" align="center">
                                              {canViewResponses && (
                                                <Tooltip title="View responses">
                                                  <ChevronRightIcon fontSize="small" color="action" />
                                                </Tooltip>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
                {pagedRows && pagedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columnCount}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        {!rows || rows.length === 0
                          ? 'No surveys yet.'
                          : tab === 'live' && !hasActiveFilters
                            ? 'No surveys are in an open live window.'
                            : 'No surveys match your filters.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={PAGE_SIZE}
              onChange={setPage}
            />
          </Box>

          {/* Row actions menu */}
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl) && Boolean(menuSurvey)}
            onClose={closeRowMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {menuSurvey && canViewResponses && (
              <MenuItem
                onClick={() => {
                  const id = menuSurvey.id;
                  closeRowMenu();
                  navigate(`/survey/${id}/responses`);
                }}
              >
                Responses{menuSurvey.response_count > 0 ? ` (${menuSurvey.response_count})` : ''}
              </MenuItem>
            )}
            {menuSurvey && canAuthor && !isSurveyClosed(menuSurvey) && (
              <MenuItem
                onClick={() => {
                  const id = menuSurvey.id;
                  closeRowMenu();
                  navigate(`/survey/${id}/edit`);
                }}
              >
                Edit
              </MenuItem>
            )}
            {menuSurvey && canPreview && (
              <MenuItem
                onClick={() => {
                  const id = menuSurvey.id;
                  closeRowMenu();
                  navigate(`/survey/${id}/preview`);
                }}
              >
                Preview
              </MenuItem>
            )}
            {menuSurvey && canAuthor && canManageSchedule(menuSurvey) && (
              <MenuItem
                onClick={() => {
                  const s = menuSurvey;
                  closeRowMenu();
                  openScheduleModal(s);
                }}
              >
                {menuSurvey.is_recurrence_parent ? 'Recurrence' : 'Schedule'}
              </MenuItem>
            )}
            {menuSurvey && menuSurvey.is_recurrence_parent && (
              <MenuItem
                onClick={() => {
                  const s = menuSurvey;
                  closeRowMenu();
                  void toggleExpand(s);
                }}
              >
                {expandedIds.has(menuSurvey.id) ? 'Hide cycles' : 'Show cycles'}
                {(menuSurvey.recurrence_instance_count ?? 0) > 0
                  ? ` (${menuSurvey.recurrence_instance_count})`
                  : ''}
              </MenuItem>
            )}
            {menuSurvey && canAuthor && (
              <MenuItem
                onClick={() => {
                  const s = menuSurvey;
                  closeRowMenu();
                  setPromptCloneSurvey(s);
                  setPromptCloneTitle(s.title ? `${s.title} (copy)` : '(copy)');
                  setPromptCloneError(null);
                }}
              >
                Clone
              </MenuItem>
            )}
            {menuSurvey &&
              canAuthor &&
              menuSurvey.active_public_token &&
              (menuSurvey.is_live_window_open || menuSurvey.live_window_state === 'not_open') && (
                <MenuItem
                  onClick={() => {
                    const s = menuSurvey;
                    closeRowMenu();
                    setShareSurvey(s);
                  }}
                >
                  Share
                </MenuItem>
              )}
            {menuSurvey && canAuthor && menuSurvey.is_live_window_open && (
              <MenuItem
                sx={{ color: 'error.main' }}
                onClick={() => {
                  setConfirmEndLiveId(menuSurvey.id);
                  setConfirmAnchorEl(menuAnchorEl);
                  setMenuSurvey(null);
                  setMenuAnchorEl(null);
                }}
              >
                End live
              </MenuItem>
            )}
            {menuSurvey && canAuthor && !menuSurvey.is_live_window_open && !menuSurvey.is_recurrence_parent && (
              <MenuItem
                sx={{ color: 'error.main' }}
                onClick={() => {
                  setConfirmDeleteId(menuSurvey.id);
                  setConfirmDeleteAnchor(menuAnchorEl);
                  setMenuSurvey(null);
                  setMenuAnchorEl(null);
                }}
              >
                Delete
              </MenuItem>
            )}
          </Menu>

          {/* Confirm: end live */}
          <Popover
            open={confirmOpen}
            anchorEl={confirmAnchorEl}
            onClose={() => {
              setConfirmEndLiveId(null);
              setConfirmAnchorEl(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Box sx={{ p: 2, maxWidth: 280 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                End live survey?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                This will stop accepting responses at the public link. You can publish again later to reopen it.
              </Typography>
              <div className="builder-modal-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setConfirmEndLiveId(null);
                    setConfirmAnchorEl(null);
                  }}
                >
                  Keep live
                </button>
                <button type="button" className="btn danger" onClick={() => void endLive(confirmEndLiveId)}>
                  End live
                </button>
              </div>
            </Box>
          </Popover>

          {/* Confirm: delete */}
          <Popover
            open={confirmDeleteOpen}
            anchorEl={confirmDeleteAnchor}
            onClose={() => {
              setConfirmDeleteId(null);
              setConfirmDeleteAnchor(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Box sx={{ p: 2, maxWidth: 280 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Delete survey?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                This removes the survey from your list. You cannot delete a survey that is currently live.
              </Typography>
              <div className="builder-modal-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setConfirmDeleteId(null);
                    setConfirmDeleteAnchor(null);
                  }}
                >
                  Cancel
                </button>
                <button type="button" className="btn danger" onClick={() => void deleteSurvey(confirmDeleteId)}>
                  Delete
                </button>
              </div>
            </Box>
          </Popover>

          {/* Clone prompt modal */}
          {promptCloneSurvey && (
            <div
              className="modal-backdrop"
              role="dialog"
              aria-modal="true"
              aria-labelledby="clone-survey-title"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget && !promptCloneSaving) {
                  setPromptCloneSurvey(null);
                }
              }}
            >
              <div className="modal-card card" onMouseDown={(e) => e.stopPropagation()}>
                <h2 id="clone-survey-title">Clone survey</h2>
                <p className="muted small">
                  Enter a name for the cloned survey
                </p>
                <label className="field">
                  <span>New survey name</span>
                  <input
                    value={promptCloneTitle}
                    onChange={(e) => {
                      setPromptCloneTitle(e.target.value);
                      setPromptCloneError(null);
                    }}
                    maxLength={500}
                    disabled={promptCloneSaving}
                    autoFocus
                    aria-invalid={Boolean(promptCloneError)}
                    aria-describedby="clone-survey-hint"
                  />
                </label>
                {promptCloneError ? (
                  <p id="clone-survey-hint" className="field-error">{promptCloneError}</p>
                ) : null}
                <p className="field-error" role="alert" style={{ marginTop: '0.5rem' }}>
                  This name cannot be changed later.
                </p>
                <div className="builder-modal-actions">
                  <button
                    type="button"
                    className="btn"
                    disabled={promptCloneSaving}
                    onClick={() => setPromptCloneSurvey(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn primary"
                    disabled={promptCloneSaving}
                    onClick={async () => {
                      const t = (promptCloneTitle || '').trim();
                      if (!t) {
                        setPromptCloneError('Please enter a name.');
                        return;
                      }
                      setPromptCloneSaving(true);
                      try {
                        await cloneSurvey(promptCloneSurvey.id, t);
                        setPromptCloneSurvey(null);
                      } catch {
                        /* error handled in cloneSurvey */
                      } finally {
                        setPromptCloneSaving(false);
                      }
                    }}
                  >
                    {promptCloneSaving ? 'Cloning…' : 'Clone'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Schedule / recurrence modal (unchanged behavior) */}
           {scheduleOpen && scheduleSurvey && (
             <div
               className="modal-backdrop modal-backdrop--publish"
               role="dialog"
               aria-modal="true"
               aria-labelledby="manage-schedule-title"
               onMouseDown={(e) => {
                 if (e.target === e.currentTarget) closeScheduleModal();
               }}
             >
              <div className="modal-card card modal-card--publish" onMouseDown={(e) => e.stopPropagation()}>
                <h2 id="manage-schedule-title">
                  {scheduleSurvey.is_recurrence_parent ? 'Recurring survey' : 'Schedule survey'}
                </h2>
                <p className="muted small publish-modal-lede">
                  {scheduleMode === 'recurring' ? (
                    <>
                      <strong>{scheduleSurvey.title}</strong> is the parent template. Each cycle creates a new survey
                      (e.g. &quot;{scheduleSurvey.title} January&quot;) with its own responses.
                    </>
                  ) : (
                    <>
                      Set when <strong>{scheduleSurvey.title}</strong> accepts responses. Times are in your local time;
                      the survey closes automatically at the close time.
                    </>
                  )}
                </p>
                {!scheduleSurvey.is_recurrence_parent && (
                  <div className="schedule-mode-toggle" role="radiogroup" aria-label="Schedule type">
                    <label className="schedule-mode-option">
                      <input
                        type="radio"
                        name="scheduleMode"
                        value="once"
                        checked={scheduleMode === 'once'}
                        onChange={() => setScheduleMode('once')}
                      />
                      One-time window
                    </label>
                    <label className="schedule-mode-option">
                      <input
                        type="radio"
                        name="scheduleMode"
                        value="recurring"
                        checked={scheduleMode === 'recurring'}
                        onChange={() => setScheduleMode('recurring')}
                      />
                      Recurring
                    </label>
                  </div>
                )}
                <form onSubmit={(e) => void saveSchedule(e)}>
                  <div className="publish-modal-schedule">
                    {scheduleMode === 'recurring' && (
                      <>
                        <label className="field">
                          <span>Repeats</span>
                          <select value={recurrenceFrequency} onChange={(e) => setRecurrenceFrequency(e.target.value)}>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="yearly">Yearly</option>
                          </select>
                        </label>
                        {recurrenceFrequency !== 'daily' && (
                          <>
                            <label className="field">
                              <span>Open for (days per cycle)</span>
                              <input
                                type="number"
                                min={1}
                                max={366}
                                required
                                value={windowDurationDays}
                                onChange={(e) => setWindowDurationDays(Number(e.target.value))}
                              />
                            </label>
                            <label className="publish-modal-notify-toggle">
                              <input
                                type="checkbox"
                                checked={!excludeWeekends}
                                onChange={(e) => setExcludeWeekends(!e.target.checked)}
                              />
                              Count weekends
                            </label>
                            {excludeWeekends && (
                              <p className="muted small">
                                Saturdays and Sundays don&apos;t count toward the days open — the close date moves out
                                past any weekends in the window.
                              </p>
                            )}
                          </>
                        )}
                        {recurrenceFrequency === 'daily' && (
                          <>
                            <label className="field">
                              <span>Opens daily at (local time)</span>
                              <input
                                type="time"
                                required
                                value={dailyOpenTime}
                                onChange={(e) => setDailyOpenTime(e.target.value)}
                              />
                              <span className="muted small publish-modal-utc">UTC: {localTimeToUtcHHMM(dailyOpenTime) || '—'}</span>
                            </label>
                            <label className="field">
                              <span>Closes daily at (local time)</span>
                              <input
                                type="time"
                                required
                                value={dailyCloseTime}
                                onChange={(e) => setDailyCloseTime(e.target.value)}
                              />
                              <span className="muted small publish-modal-utc">UTC: {localTimeToUtcHHMM(dailyCloseTime) || '—'}</span>
                            </label>
                            {dailyOpenTime && dailyCloseTime && dailyOpenTime === dailyCloseTime && (
                              <span className="field-error">Open and close times must be different.</span>
                            )}
                            {dailyOpenTime && dailyCloseTime && dailyCloseTime < dailyOpenTime && (
                              <p className="muted small">
                                Close time is before open time — the survey stays open overnight into the next day.
                              </p>
                            )}
                          </>
                        )}
                        <label className="field">
                          <span>Series starts (local time)</span>
                          <input
                            type="datetime-local"
                            required
                            value={scheduleOpensAt}
                            onChange={(e) => setScheduleOpensAt(e.target.value)}
                          />
                        </label>
                        <label className="field">
                          <span>Series ends (local time, optional)</span>
                          <input
                            type="datetime-local"
                            value={seriesEndsAt}
                            onChange={(e) => setSeriesEndsAt(e.target.value)}
                          />
                          {seriesEndsAt && scheduleOpensAt && new Date(seriesEndsAt) <= new Date(scheduleOpensAt) ? (
                            <span className="field-error">Series end must be after the series start.</span>
                          ) : (
                            <span className="muted small publish-modal-utc">
                              {seriesEndsAt ? `UTC: ${formatUtcPreview(seriesEndsAt)}` : 'Leave empty to repeat until paused.'}
                            </span>
                          )}
                        </label>
                      </>
                    )}
                    {scheduleMode === 'once' && !scheduleSurvey.is_recurrence_parent && (
                      <>
                        <label className="field">
                          <span>Opens at (local time)</span>
                          <input
                            type="datetime-local"
                            required
                            value={scheduleOpensAt}
                            onChange={(e) => setScheduleOpensAt(e.target.value)}
                          />
                          <span className="muted small publish-modal-utc">UTC: {formatUtcPreview(scheduleOpensAt)}</span>
                        </label>
                        <label className="field">
                          <span>Closes at (local time)</span>
                          <input
                            type="datetime-local"
                            required
                            value={scheduleClosesAt}
                            onChange={(e) => setScheduleClosesAt(e.target.value)}
                          />
                          <span className="muted small publish-modal-utc">UTC: {formatUtcPreview(scheduleClosesAt)}</span>
                        </label>
                      </>
                    )}
                    {scheduleSurvey.is_recurrence_parent && (
                      <p className="muted small">
                        Recurrence is active ({scheduleSurvey.recurrence_frequency}). Expand the survey row to see its
                        cycles. Pause to stop spawning new cycles.
                      </p>
                    )}
                  </div>
                  <div className="builder-modal-actions publish-modal-actions">
                    <button type="button" className="btn" disabled={scheduleSaving} onClick={closeScheduleModal}>
                      Cancel
                    </button>
                    {scheduleSurvey.is_recurrence_parent ? (
                      <button
                        type="button"
                        className="btn danger"
                        onClick={() => {
                          void pauseRecurrence(scheduleSurvey.id);
                          closeScheduleModal();
                        }}
                      >
                        Pause recurrence
                      </button>
                    ) : (
                      <button type="submit" className="btn primary" disabled={scheduleSaving}>
                        {scheduleSaving ? 'Saving…' : scheduleMode === 'recurring' ? 'Start recurring' : 'Save schedule'}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          )}

          <SurveyLinkDialog
            open={Boolean(linkSurvey)}
            onClose={() => setLinkSurvey(null)}
            surveyTitle={linkSurvey?.title}
            publicToken={linkSurvey?.active_public_token}
            onCopySuccess={() => setCopyToastKey(Date.now())}
            onCopyError={handleShareCopyError}
          />
          <SurveyShareDialog
            open={Boolean(shareSurvey)}
            onClose={() => setShareSurvey(null)}
            surveyId={shareSurvey?.id}
            surveyTitle={shareSurvey?.title}
            publicToken={shareSurvey?.active_public_token}
            isLiveWindowOpen={shareSurvey?.is_live_window_open}
            liveWindowState={shareSurvey?.live_window_state}
            onCopySuccess={() => setCopyToastKey(Date.now())}
            onCopyError={handleShareCopyError}
          />
        </>
      )}
    </Box>
  );
}
